import React from 'react'

import {FormattedMessage} from 'react-intl'
import {useProductList} from '../util/product-list-context'

import {
    Box,
    Text,
    VStack,
    Skeleton,
    Flex,
    Button
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Custom Components
import ProductListClassicSubset from './product-list-classic-subset'
import RefinementsDrawer from './refinements-drawer'
import {Filter2Icon} from '../../../components/custom-icons'
import MetaTagsComponent from '../../../components/metatags'

/**
 * A React functional component that renders a search product list page layout.
 *
 * @returns {JSX.Element} A JSX element representing the product list page layout.
 */
const ProductListSearch = () => {
    const {
        searchQuery,
        isProductListLoading,
        productSearchResult,
        onOpenRefinements,
        refinementsOpenRef,
        metatags
    } = useProductList()

    const finalMetadata = [
        {
            id: 'description',
            value: metatags?.find((tag) => tag.id === 'description')?.value || searchQuery
        },
        {
            id: 'title',
            value: metatags?.find((tag) => tag.id === 'title')?.value || searchQuery
        },
        {
            id: 'keywords',
            value: metatags?.find((tag) => tag.id === 'keywords')?.value || ''
        },
        {
            id: 'robots',
            value: 'noindex'
        }
    ]

    return (
        <Box as="section" borderTopWidth={1} borderColor="borderGray">
            <MetaTagsComponent searchQuery={searchQuery} metaData={finalMetadata} />
            {/* Top PLP panel */}
            <Flex
                direction="row"
                justify="space-between"
                align="center"
                gap="6px"
                px={{base: 10, md: 20}}
                py={{base: '44px', md: '34px'}}
                position="sticky"
                // header height as top
                top={{base: '64px', md: '52px'}}
                bg="white"
                zIndex={1}
            >
                <VStack align="flex-start">
                    {searchQuery && (
                        <Text as="h1" textStyle="bodyLarge2">
                            <FormattedMessage
                                defaultMessage='Results for "{search}"'
                                id="product_list.search.results_for"
                                values={{search: searchQuery}}
                            />
                        </Text>
                    )}

                    {/* Total */}
                    {isProductListLoading ? (
                        <Skeleton height="20px" width="80px" />
                    ) : (
                        productSearchResult && (
                            <Text variant="bodyBase2" color="darkGray" whiteSpace="nowrap">
                                <FormattedMessage
                                    defaultMessage='Showing {total} results for "{search}"'
                                    id="product_list.search.total"
                                    values={{
                                        total: productSearchResult?.total || 0,
                                        search: searchQuery
                                    }}
                                />
                            </Text>
                        )
                    )}
                </VStack>

                {!isProductListLoading && (
                    <Button
                        variant="primary"
                        height="40px"
                        rightIcon={<Filter2Icon boxSize={4} />}
                        onClick={onOpenRefinements}
                        ref={refinementsOpenRef}
                    >
                        <FormattedMessage defaultMessage="Filter" id="product_list.button.filter" />
                    </Button>
                )}
            </Flex>

            {/* PLP Body: Product tiles grid  */}
            {productSearchResult?.hits && (
                <ProductListClassicSubset subSet={productSearchResult.hits} />
            )}

            {/* Drawer for filter options */}
            <RefinementsDrawer />
        </Box>
    )
}

ProductListSearch.propTypes = {}

export default ProductListSearch
